import { useEffect, useState } from 'react';

/**
 * useState that survives reloads and page changes by mirroring into localStorage.
 *
 * `isValid` guards against stale shapes: a persisted value written by an older build
 * (or hand-edited) falls back to the initial value instead of poisoning the UI.
 */
export function usePersistedState<T>(
  key: string,
  initialValue: T,
  isValid: (value: unknown) => value is T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return initialValue;
      }

      const parsed = JSON.parse(stored);
      return isValid(parsed) ? parsed : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full or unavailable — the filter still works for this session.
    }
  }, [key, value]);

  return [value, setValue];
}
